import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface AccountMenuProps {
  name: string;
  email?: string;
  onProfile: () => void;
  onChangePassword: () => void;
  onBilling: () => void;
  onSignOut: () => void;
}

/**
 * D19 (2026-08-10) — the account avatar + dropdown in the slim top bar.
 *
 * Everything account-shaped now lives behind one control: profile, password,
 * billing (which is also the cancellation path — see BillingDrawer), language,
 * and sign out. Previously these were scattered between the sidebar and
 * nowhere at all, and Billing in particular was unreachable.
 *
 * Closes on Escape and on outside click, and the trigger keeps focus so a
 * keyboard user can open it, tab through, and Escape back out.
 */
export const AccountMenu: FC<AccountMenuProps> = ({
  name,
  email,
  onProfile,
  onChangePassword,
  onBilling,
  onSignOut,
}) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  const initial = (name || '?').charAt(0).toUpperCase();
  const isFr = i18n.language.startsWith('fr');

  const run = (fn: () => void) => () => {
    setOpen(false);
    fn();
  };

  const setLang = (lng: 'en' | 'fr') => {
    i18n.changeLanguage(lng);
    // Match the landing page's behaviour so a fresh tab keeps the choice.
    try {
      localStorage.setItem('aidfone_language', lng);
    } catch {
      /* private mode — the in-memory change still applies */
    }
    setOpen(false);
  };

  const item =
    'w-full text-left px-4 py-2.5 text-sm text-brand-text hover:bg-brand-bg-warm transition-colors flex items-center gap-2.5';

  return (
    <div className="relative" ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('dashboard.accountMenu.open')}
        className="w-9 h-9 rounded-full bg-brand-primary/10 text-brand-primary font-semibold flex items-center justify-center hover:bg-brand-primary/20 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
      >
        {initial}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-64 bg-white rounded-2xl border border-brand-border shadow-xl overflow-hidden z-50 animate-fadeIn"
        >
          {/* Profile block — identity first, so the caregiver can see which
              account they are in before acting on it. */}
          <button type="button" role="menuitem" onClick={run(onProfile)} className={`${item} flex-col !items-start gap-0.5 py-3`}>
            <span className="font-semibold text-brand-text truncate w-full">{name}</span>
            {email && <span className="text-xs text-brand-text-muted truncate w-full">{email}</span>}
          </button>

          <div className="h-px bg-brand-border" />

          <button type="button" role="menuitem" onClick={run(onChangePassword)} className={item}>
            <span className="material-symbols-outlined text-brand-text-muted" style={{ fontSize: '18px' }}>lock</span>
            {t('dashboard.accountMenu.changePassword')}
          </button>

          <button type="button" role="menuitem" onClick={run(onBilling)} className={item}>
            <span className="material-symbols-outlined text-brand-text-muted" style={{ fontSize: '18px' }}>credit_card</span>
            {t('dashboard.accountMenu.billing')}
          </button>

          <div className="h-px bg-brand-border" />

          {/* Language — a segmented pair rather than a toggle, so the current
              choice is legible without reading the control's state. */}
          <div className="px-4 py-2.5 flex items-center gap-2.5">
            <span className="material-symbols-outlined text-brand-text-muted" style={{ fontSize: '18px' }}>language</span>
            <div className="flex rounded-lg border border-brand-border overflow-hidden">
              {(['en', 'fr'] as const).map((lng) => (
                <button
                  key={lng}
                  type="button"
                  onClick={() => setLang(lng)}
                  aria-pressed={lng === 'fr' ? isFr : !isFr}
                  className={`px-3 py-1 text-xs font-semibold transition-colors ${
                    (lng === 'fr') === isFr
                      ? 'bg-brand-primary text-white'
                      : 'bg-white text-brand-text-muted hover:bg-brand-bg-warm'
                  }`}
                >
                  {lng.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <div className="h-px bg-brand-border" />

          <button type="button" role="menuitem" onClick={run(onSignOut)} className={`${item} text-red-700`}>
            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>logout</span>
            {t('dashboard.accountMenu.signOut')}
          </button>
        </div>
      )}
    </div>
  );
};

export default AccountMenu;
